// Mes dossiers : demandes en cours et terminées, statut usager en texte, échéance, action attendue
// mise en évidence. Les brouillons apparaissent avec les demandes en cours.
import { EtatVide, LienBouton } from '@cc/ui';
import { FolderOpen } from 'lucide-react';
import { useState } from 'react';
import { EtatRequete } from '../../../commun/EtatRequete';
import { date } from '../../../commun/format';
import { useMesDossiers } from '../api';
import { useCollectiviteChoisie } from '../collectivite';
import { EnTeteCitoyen, ExigeConnexion, Ligne, Liste, TexteStatut } from '../commun';
import { actionAttendue, enCours } from '../statuts';
import type { ResumeDossierUsager } from '../types';
import styles from '../citoyen.module.css';

function sousTitre(d: ResumeDossierUsager): string {
  const parts = [d.numero ?? 'Brouillon', d.collectivite.nom];
  if (d.representation) parts.push(d.representation.entite_nom);
  if (d.enregistre_le) parts.push(`déposée le ${date(d.enregistre_le)}`);
  else parts.push(`commencée le ${date(d.cree_le)}`);
  if (d.echeance_reglementaire && enCours(d)) parts.push(`réponse attendue le ${date(d.echeance_reglementaire)}`);
  return parts.join(' · ');
}

export function PageMesDossiers() {
  const dossiers = useMesDossiers();
  const [choisie] = useCollectiviteChoisie();
  const [vue, setVue] = useState<'en_cours' | 'terminees'>('en_cours');
  const liste = (dossiers.data ?? []).filter((d) => (vue === 'en_cours' ? enCours(d) : !enCours(d)));
  const nbActions = (dossiers.data ?? []).filter((d) => actionAttendue(d)).length;
  return (
    <>
      <EnTeteCitoyen titre="Mes dossiers" sousTitre={nbActions > 0 ? `${nbActions} demande${nbActions > 1 ? 's' : ''} attend${nbActions > 1 ? 'ent' : ''} une action de votre part.` : 'Toutes vos demandes, dans toutes les collectivités.'} />
      <ExigeConnexion titre="Connectez-vous pour voir vos dossiers">
        <div role="group" aria-label="Filtrer les dossiers" className={styles.filtres}>
          <button type="button" className={styles.filtre} aria-pressed={vue === 'en_cours'} onClick={() => setVue('en_cours')}>
            En cours
          </button>
          <button type="button" className={styles.filtre} aria-pressed={vue === 'terminees'} onClick={() => setVue('terminees')}>
            Terminées
          </button>
        </div>
        <EtatRequete chargement={dossiers.isPending} erreur={dossiers.error} onReessayer={() => void dossiers.refetch()} fonction="Vos dossiers" lignes={5}>
          {liste.length === 0 ? (
            <EtatVide
              titre={vue === 'en_cours' ? 'Aucune demande en cours' : 'Aucune demande terminée'}
              icone={FolderOpen}
              action={
                vue === 'en_cours' ? (
                  <LienBouton href={choisie ? `/citoyen/${choisie.code}/demarches` : '/citoyen'} variante="principal">
                    Faire une démarche
                  </LienBouton>
                ) : undefined
              }
            >
              <p>{vue === 'en_cours' ? 'Choisissez une démarche dans le catalogue de votre collectivité pour déposer une demande.' : 'Les demandes approuvées, refusées, retirées ou classées apparaîtront ici.'}</p>
            </EtatVide>
          ) : (
            <Liste libelle={vue === 'en_cours' ? 'Demandes en cours' : 'Demandes terminées'}>
              {liste.map((d) => {
                const action = actionAttendue(d);
                return (
                  <Ligne
                    key={d.id}
                    href={`/citoyen/mes-dossiers/${d.id}`}
                    titre={d.demarche.libelle}
                    sousTitre={sousTitre(d)}
                    droite={action ? <TexteStatut libelle={action} ton="attention" /> : <TexteStatut code={d.statut.code} />}
                  />
                );
              })}
            </Liste>
          )}
        </EtatRequete>
      </ExigeConnexion>
    </>
  );
}
